import { Rng } from './rng.js';
import {
  DX,
  DY,
  EFFECT_ARMOR,
  EFFECT_CARRY,
  EFFECT_DOUBLE,
  EFFECT_FIRE,
  EFFECT_FROZEN,
  EFFECT_GHOST,
  EFFECT_MAGNET,
  EFFECT_SHIELD,
  EFFECT_SPEED,
  FOOD_ARMOR,
  FOOD_DOUBLE,
  FOOD_ELECTRIC,
  FOOD_FIRE,
  FOOD_FREEZE,
  FOOD_GHOST,
  FOOD_GOLDEN,
  FOOD_MAGNET,
  FOOD_NORMAL,
  FOOD_REMAINS,
  FOOD_REMAINS_BIG,
  FOOD_SPEED,
  FOOD_TELEPORT,
  FOOD_TROPHY,
  FOOD_VALUE,
  isEdible,
  isOpposite,
  isPowerup,
  POWERUP_KINDS,
  type DeathCause,
  type Dir,
  type Food,
  type FoodKind,
  type GameConfig,
  type GamePhase,
  type GameResult,
  type PlayerInit,
  type PlayerState,
  type Standing,
  type TickEvents,
} from './types.js';

export const KILL_BONUS = 10;
/** On top of the kill bonus: killing your nemesis, or the leader. */
export const REVENGE_BONUS = 5;
export const BOUNTY_BONUS = 15;
/** Dodging a rival's body at the last moment; at most once per cooldown so it can't be farmed. */
export const CLOSE_CALL_POINTS = 2;
export const CLOSE_CALL_COOLDOWN_TICKS = 36;
const GOLDEN_LIFETIME_TICKS = 90;
const GOLDEN_SPAWN_CHANCE = 1 / 140;
export const MAX_PENDING_TURNS = 2;
export const MAX_MULTIPLIER = 5;
/** Ticks after eating during which the next apple raises the multiplier. */
export const COMBO_WINDOW_TICKS = 48;
/** A rock spawns after every N apples when obstacles are on. */
export const OBSTACLE_EVERY = 4;
const MAX_OBSTACLES = 80;
/** How long a power-up effect lasts once eaten (8 s at 12 Hz). */
export const POWERUP_DURATION_TICKS = 96;
const POWERUP_LIFETIME_TICKS = 180;
const POWERUP_SPAWN_CHANCE = 1 / 70;
const MAX_POWERUPS = 2;

/** Boost: every this many boosted ticks the snake loses a tail segment (dropped as food). */
export const BOOST_COST_TICKS = 4;
/** Snakes at least this long leave big food behind when they die. */
export const BIG_REMAINS_LENGTH = 20;
/** The boss (respawn arenas): how long it is, what taking it down is worth, and how long until it's back. */
export const BOSS_LENGTH = 30;
export const BOSS_BONUS = 40;
export const BOSS_RESPAWN_TICKS = 12 * 45;
/** Food a snake leaves behind (dying or boosting) disappears after this long (15 s at 12 Hz). */
export const REMAINS_LIFETIME_TICKS = 12 * 15;

/** Freeze: rivals whose head is within this many cells of the eater stop for FREEZE_TICKS. */
export const FREEZE_RADIUS = 10;
export const FREEZE_TICKS = 18;
/** Fire: how long a snake burns once it eats the fire power-up (6 s at 12 Hz). */
export const FIRE_TICKS = 72;
/** After armor absorbs a crash the snake is intangible this long, so it can get clear. */
export const ARMOR_GRACE_TICKS = 12;

/** King of the hill: the hill moves this often, and a lone holder scores a point every HILL_POINT_EVERY ticks. */
export const HILL_MOVE_TICKS = 12 * 30;
export const HILL_POINT_EVERY = 6;
/** Capture the food: points per delivered trophy, and deliveries that win the match outright. */
export const CAPTURE_POINTS = 50;
export const CAPTURES_TO_WIN = 3;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
export const inRect = (r: Rect, x: number, y: number) => x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;

/**
 * King of the hill: where the hill is at a tick (null when `rules.hill` is off).
 * Pure function of config + tick, so clients draw exactly what the server scores.
 * The first hill is in the middle; later ones are placed from the seed.
 */
export function hillAt(config: GameConfig, tick: number): Rect | null {
  if (!config.rules?.hill) return null;
  const size = Math.max(4, Math.floor(Math.min(config.width, config.height) / 6));
  const index = Math.max(0, Math.floor((tick - config.countdownTicks) / HILL_MOVE_TICKS));
  if (index === 0) return { x: Math.floor((config.width - size) / 2), y: Math.floor((config.height - size) / 2), w: size, h: size };
  const rng = new Rng((config.seed ^ Math.imul(index, 0x9e3779b1)) >>> 0);
  const margin = 3;
  return {
    x: margin + rng.int(Math.max(1, config.width - size - 2 * margin)),
    y: margin + rng.int(Math.max(1, config.height - size - 2 * margin)),
    w: size,
    h: size,
  };
}

/** Ticks until the hill next moves (null when there is no hill). */
export function ticksToHillMove(config: GameConfig, tick: number): number | null {
  if (!config.rules?.hill) return null;
  const running = Math.max(0, tick - config.countdownTicks);
  return HILL_MOVE_TICKS - (running % HILL_MOVE_TICKS);
}

/** Capture the food: team 0's base on the left edge, team 1's on the right (empty when `rules.ctf` is off). */
export function ctfBases(config: GameConfig): Rect[] {
  if (!config.rules?.ctf) return [];
  const w = Math.max(4, Math.floor(config.width / 8));
  const h = Math.max(6, Math.floor(config.height / 3));
  const y = Math.floor((config.height - h) / 2);
  return [
    { x: 1, y, w, h },
    { x: config.width - 1 - w, y, w, h },
  ];
}

/** Where the trophy (re)appears: the middle of the arena. */
export const trophyHome = (config: GameConfig) => Math.floor(config.height / 2) * config.width + Math.floor(config.width / 2);

/** Battle royale: the zone starts closing after this many running ticks, then shrinks a step at a time. */
export const ZONE_START_TICKS = 12 * 20;
export const ZONE_STEP_TICKS = 12 * 4;
/** The zone never closes below this many cells across, or a quarter of the arena (long snakes need room). */
const ZONE_MIN_SIDE = 10;
/** At most this many steps to close fully (2 minutes), so a 3-minute match ends small whatever the arena size. */
const ZONE_MAX_STEPS = 30;

/** Rings the zone can close in all. */
const zoneMax = (config: GameConfig) => {
  const side = Math.min(config.width, config.height);
  return Math.max(0, Math.floor((side - Math.max(ZONE_MIN_SIDE, Math.round(side / 4))) / 2));
};

/** Rings closed per step: 1 on a small arena, more on a big one so it still closes in time. */
export const zoneRing = (config: GameConfig) => Math.max(1, Math.ceil(zoneMax(config) / ZONE_MAX_STEPS));

/** Zone steps taken by a tick (0 before it starts closing). */
function zoneSteps(config: GameConfig, tick: number): number {
  const running = tick - config.countdownTicks - ZONE_START_TICKS;
  return running < 0 ? 0 : 1 + Math.floor(running / ZONE_STEP_TICKS);
}

/**
 * Rings closed by the battle-royale zone at a tick (0 when `rules.shrink` is off).
 * Pure function of config + tick so clients draw exactly what the server enforces.
 */
export function zoneInset(config: GameConfig, tick: number): number {
  if (!config.rules?.shrink) return 0;
  return Math.min(zoneMax(config), zoneSteps(config, tick) * zoneRing(config));
}

/** Ticks until the zone next closes in, or null if it is not closing any further. */
export function ticksToNextZone(config: GameConfig, tick: number): number | null {
  if (!config.rules?.shrink) return null;
  if (zoneInset(config, tick) >= zoneMax(config)) return null;
  return config.countdownTicks + ZONE_START_TICKS + zoneSteps(config, tick) * ZONE_STEP_TICKS - tick;
}

export const insideZone = (x: number, y: number, width: number, height: number, inset: number) =>
  x >= inset && y >= inset && x < width - inset && y < height - inset;

/** Bitmask of a player's active power-up effects at `tick`. */
export function effectsOf(p: PlayerState, tick: number): number {
  return (
    (p.ghostUntil > tick ? EFFECT_GHOST : 0) |
    (p.doubleUntil > tick ? EFFECT_DOUBLE : 0) |
    (p.magnetUntil > tick ? EFFECT_MAGNET : 0) |
    (p.shieldUntil > tick ? EFFECT_SHIELD : 0) |
    (p.speedUntil > tick || p.boosting ? EFFECT_SPEED : 0) |
    (p.armor ? EFFECT_ARMOR : 0) |
    (p.frozenUntil > tick ? EFFECT_FROZEN : 0) |
    (p.carrying ? EFFECT_CARRY : 0) |
    (p.fireUntil > tick ? EFFECT_FIRE : 0)
  );
}

/** Spawn anchors as fractions of the arena, with the initial heading. Snakes start facing inward. */
const SPAWNS: Array<[number, number, Dir]> = [
  [0.14, 0.25, 1],
  [0.86, 0.75, 3],
  [0.75, 0.14, 2],
  [0.25, 0.86, 0],
  [0.14, 0.75, 1],
  [0.86, 0.25, 3],
  [0.25, 0.14, 2],
  [0.75, 0.86, 0],
];
export const MAX_PLAYERS = SPAWNS.length;
/** Respawn arenas place snakes anywhere free, so they can hold more (mostly bots). */
export const MAX_ARENA_PLAYERS = 24;
/** Spawn protection and respawn delay (3 s at 12 Hz). */
export const SHIELD_TICKS = 36;
export const RESPAWN_TICKS = 36;

/** Every mode plays on a big, scrolling arena (slither.io style) that grows with the snake count. */
export function arenaSizeFor(playerCount: number): { width: number; height: number } {
  const side = Math.min(170, Math.max(120, 96 + playerCount * 5));
  return { width: side, height: side };
}

export function defaultConfig(playerCount: number, overrides: Partial<GameConfig> = {}): GameConfig {
  const { width, height } = arenaSizeFor(playerCount);
  return {
    width,
    height,
    tickRate: 12,
    durationTicks: 12 * 180,
    mode: 'ffa',
    // Plenty of food: roughly one apple per 45 cells.
    foodTarget: Math.round((width * height) / 45),
    startLength: 4,
    seed: (Math.random() * 2 ** 32) >>> 0,
    countdownTicks: 36,
    ...overrides,
  };
}

/**
 * Authoritative, deterministic Snake simulation.
 *
 * All snakes move simultaneously each tick. Collisions are resolved against
 * the board *after* tails that are about to move have been vacated, so
 * chasing another snake's tail is legal, while head-on and head-swap
 * collisions kill both snakes.
 */
export class GameEngine {
  readonly config: GameConfig;
  readonly players: PlayerState[] = [];
  readonly food = new Map<number, Food>();
  readonly obstacles = new Set<number>();
  tick = 0;
  phase: GamePhase = 'countdown';
  private readonly rng: Rng;
  private readonly occupancy: Int16Array;
  /**
   * Where food may not spawn (see markBlocked): cells holding `blockedStamp`. Worked out once for a
   * whole batch of spawns, not once per food: a big arena refills dozens of food in a tick, and
   * collecting every body into a fresh Set for each one was a sixth of the server's tick.
   */
  private readonly blockedAt: Uint32Array;
  private blockedStamp = 0;
  /** blockedAt is up to date: nothing has moved, died or respawned since it was marked. */
  private blockedFresh = false;
  private readonly pendingEliminations = new Set<number>();
  /** Snakes hit by an electric shock this tick (reported at the end of the tick). */
  private readonly shockedNow = new Set<number>();
  private result: GameResult | null = null;

  constructor(config: GameConfig, players: PlayerInit[]) {
    const max = config.rules?.respawn ? MAX_ARENA_PLAYERS : MAX_PLAYERS;
    if (players.length < 1 || players.length > max) {
      throw new Error(`player count must be 1..${max}`);
    }
    if (config.width * config.height > 0xfffe) throw new Error('arena too large');
    this.config = config;
    this.rng = new Rng(config.seed);
    this.occupancy = new Int16Array(config.width * config.height);
    this.blockedAt = new Uint32Array(config.width * config.height);
    for (const [i, p] of players.entries()) this.players.push(this.spawn(p, i));
    if (config.countdownTicks <= 0) this.phase = 'running';
    for (let i = 0; i < config.foodTarget; i++) this.spawnFood(FOOD_NORMAL);
    if (config.rules?.ctf) this.placeTrophy(trophyHome(config));
  }


  private spawn(init: PlayerInit, index: number): PlayerState {
    const { width, height, startLength } = this.config;
    let body: number[];
    let dir: Dir;
    if (this.config.rules?.respawn) {
      ({ body, dir } = this.findSpawn(this.spawnSide(init.team), this.lengthFor(init)));
    } else {
      const [fx, fy, d] = SPAWNS[index]!;
      dir = d;
      const hx = Math.round(fx * (width - 1));
      const hy = Math.round(fy * (height - 1));
      body = [];
      for (let i = 0; i < startLength; i++) {
        body.push((hy - DY[dir] * i) * width + (hx - DX[dir] * i));
      }
    }
    return {
      ...init,
      isBot: init.isBot ?? false,
      alive: true,
      body,
      dir,
      pendingDirs: [],
      grow: 0,
      score: 0,
      kills: 0,
      foodEaten: 0,
      maxLength: this.lengthFor(init),
      deathTick: null,
      deathCause: null,
      killedBy: null,
      multiplier: 1,
      maxMultiplier: 1,
      comboUntil: 0,
      ghostUntil: 0,
      doubleUntil: 0,
      magnetUntil: 0,
      speedUntil: 0,
      frozenUntil: 0,
      fireUntil: 0,
      armor: false,
      teleportPending: false,
      powerupsTaken: 0,
      carrying: false,
      captures: 0,
      hillTicks: 0,
      boosting: false,
      boostTicks: 0,
      shieldUntil: this.config.rules?.spawnShield ? this.config.countdownTicks + SHIELD_TICKS : 0,
      respawnAt: null,
      deaths: 0,
      livesLeft: init.lives ?? null,
      nemesis: null,
      lastCloseCall: -CLOSE_CALL_COOLDOWN_TICKS,
    };
  }

  /** How long a snake (re)spawns: the boss is a giant, only in respawn arenas. */
  private lengthFor(p: { boss?: boolean }): number {
    return p.boss && this.config.rules?.respawn ? BOSS_LENGTH : this.config.startLength;
  }

  /** Capture the food: snakes (re)spawn on their own team's half. */
  private spawnSide(team: number): 0 | 1 | null {
    return this.config.rules?.ctf && this.config.mode === 'teams' ? (team === 1 ? 1 : 0) : null;
  }

  /**
   * A free spot for a (re)spawning snake in a respawn arena: the body, the cells
   * ahead of the head and a margin around it are empty, and no other head is close.
   * Falls back to looser checks on a crowded board. `side` keeps it on one half.
   */
  private findSpawn(side: 0 | 1 | null = null, startLength = this.config.startLength): { body: number[]; dir: Dir } {
    const { width, height } = this.config;
    const blocked = new Set<number>();
    const heads: number[] = [];
    for (const p of this.players) {
      if (!p.alive) continue;
      for (const c of p.body) blocked.add(c);
      heads.push(p.body[0]!);
    }
    for (const c of this.obstacles) blocked.add(c);
    const inset = zoneInset(this.config, this.tick + RESPAWN_TICKS);
    const margin = 3 + inset;
    const lo = margin + startLength;
    const span = Math.max(1, width - 2 * lo);
    for (let attempt = 0; attempt < 240; attempt++) {
      const strict = attempt < 160;
      const hx =
        side === null ? lo + this.rng.int(span) : side === 0 ? lo + this.rng.int(Math.max(1, Math.floor(span / 2))) : lo + Math.floor(span / 2) + this.rng.int(Math.max(1, Math.ceil(span / 2)));
      const hy = margin + startLength + this.rng.int(Math.max(1, height - 2 * (margin + startLength)));
      // Face roughly toward the middle so a fresh snake doesn't start at a wall.
      const dx = width / 2 - hx;
      const dy = height / 2 - hy;
      const dir: Dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 2 : 0;
      const cells: number[] = [];
      for (let i = -6; i < startLength; i++) {
        const x = hx - DX[dir] * i;
        const y = hy - DY[dir] * i;
        if (x < 0 || y < 0 || x >= width || y >= height) break;
        cells.push(y * width + x);
      }
      if (cells.length < startLength + 6 || cells.some((c) => blocked.has(c))) continue;
      const near = strict ? 10 : 4;
      if (heads.some((h) => Math.abs((h % width) - hx) + Math.abs(Math.floor(h / width) - hy) < near)) continue;
      return { body: cells.slice(6), dir };
    }
    // Board is packed: take any empty straight run.
    for (let cell = 0; cell < width * height; cell++) {
      const x = cell % width;
      const y = Math.floor(cell / width);
      if (x < startLength + 1 || x >= width - 2) continue;
      const body = Array.from({ length: startLength }, (_, i) => y * width + x - i);
      if (body.every((c) => !blocked.has(c))) return { body, dir: 1 };
    }
    return { body: [Math.floor(height / 2) * width + Math.floor(width / 2)], dir: 1 };
  }

  /** In play or coming back (respawn arenas). */
  isActive(p: PlayerState): boolean {
    return p.alive || p.respawnAt !== null;
  }

  /**
   * Persistent arenas: take a slot out of play without a death (no food, no kill
   * credit). Clients see it disappear on the next delta.
   */
  park(slot: number): void {
    const p = this.players[slot];
    if (!p) return;
    p.alive = false;
    p.body = [];
    p.pendingDirs = [];
    p.respawnAt = null;
    p.carrying = false;
  }

  /**
   * Persistent arenas: hand a slot to a new owner (a joining player, or a bot
   * taking over from someone who left). Stats start from zero and the snake
   * respawns somewhere free, shielded, on the next tick.
   */
  activate(slot: number, owner: { userId: string; name: string; skin: string; isBot: boolean; lives?: number | null }): void {
    const p = this.players[slot];
    if (!p) return;
    this.park(slot);
    const { lives, ...who } = owner;
    Object.assign(p, who, {
      livesLeft: lives ?? null,
      score: 0,
      kills: 0,
      foodEaten: 0,
      maxLength: this.config.startLength,
      deaths: 0,
      multiplier: 1,
      maxMultiplier: 1,
      powerupsTaken: 0,
      captures: 0,
      hillTicks: 0,
      boosting: false,
      boostTicks: 0,
      deathTick: null,
      deathCause: null,
      killedBy: null,
      respawnAt: this.tick,
      nemesis: null,
    });
    // A new owner: nobody has a score to settle with them yet.
    for (const o of this.players) if (o.nemesis === slot) o.nemesis = null;
  }

  /** Standings as they are right now (the match may still be running). */
  currentResult(): GameResult {
    return this.result ?? this.computeResult();
  }

  get tickMs(): number {
    return 1000 / this.config.tickRate;
  }

  cellX(cell: number): number {
    return cell % this.config.width;
  }
  cellY(cell: number): number {
    return Math.floor(cell / this.config.width);
  }

  /**
   * Buffer a turn. Rejects reversing into yourself and duplicates, validated
   * against the last *queued* direction so fast double-turns work.
   */
  queueInput(slot: number, dir: Dir): boolean {
    const p = this.players[slot];
    if (!p || !p.alive || this.phase === 'finished') return false;
    if (dir < 0 || dir > 3 || !Number.isInteger(dir)) return false;
    const base = p.pendingDirs.length ? p.pendingDirs[p.pendingDirs.length - 1]! : p.dir;
    if (dir === base || isOpposite(dir, base)) return false;
    if (p.pendingDirs.length >= MAX_PENDING_TURNS) return false;
    p.pendingDirs.push(dir);
    return true;
  }

  /** Hold or release boost (ignored unless `rules.boost`; needs a little length to spend). */
  setBoost(slot: number, on: boolean): void {
    const p = this.players[slot];
    if (p) p.boosting = on && !!this.config.rules?.boost;
  }

  private canBoost(p: PlayerState): boolean {
    return p.boosting && p.alive && p.body.length > this.config.startLength + 1;
  }

  /** Remove a player at the next tick (disconnect, forfeit). */
  eliminate(slot: number): void {
    const p = this.players[slot];
    if (p?.alive) this.pendingEliminations.add(slot);
  }

  /** End the match now (e.g. only bots remain). Survivors are ranked as usual. */
  forceFinish(): TickEvents {
    const ev = this.emptyEvents(this.tick);
    ev.finished = true;
    if (this.phase !== 'finished') {
      this.phase = 'finished';
      this.result = this.computeResult();
    }
    return ev;
  }

  isFinished(): boolean {
    return this.phase === 'finished';
  }

  getResult(): GameResult | null {
    return this.result;
  }

  private emptyEvents(tick: number): TickEvents {
    return { tick, moved: [], extraMoves: [], died: [], foodAdded: [], foodRemoved: [], eaten: [], obstaclesAdded: [], respawned: [], trimmed: [], shocked: [], bonuses: [], finished: false };
  }

  private isGhost(p: PlayerState): boolean {
    return p.ghostUntil > this.tick || p.shieldUntil > this.tick;
  }

  step(): TickEvents {
    const ev = this.emptyEvents(++this.tick);
    if (this.phase === 'finished') {
      ev.finished = true;
      return ev;
    }

    const deaths = new Map<number, Death>();
    for (const slot of this.pendingEliminations) {
      if (this.players[slot]?.alive) deaths.set(slot, { cause: 'disconnect', killedBy: null });
    }
    this.pendingEliminations.clear();

    if (this.phase === 'countdown') {
      if (deaths.size) this.applyDeaths(deaths, ev);
      if (this.tick >= this.config.countdownTicks) this.phase = 'running';
      this.checkFinished(ev);
      return ev;
    }

    const { width, height } = this.config;
    const rules = this.config.rules ?? {};
    const inset = zoneInset(this.config, this.tick);
    if (inset > zoneInset(this.config, this.tick - 1)) {
      // The zone just closed in: food caught outside it is gone.
      for (const cell of this.food.keys()) {
        if (!insideZone(cell % width, Math.floor(cell / width), width, height, inset)) {
          this.food.delete(cell);
          ev.foodRemoved.push(cell);
        }
      }
    }

    // Frozen snakes stay where they are (and block like any other body).
    this.advance(
      this.players.filter((p) => p.alive && !deaths.has(p.slot) && p.frozenUntil <= this.tick),
      deaths,
      ev,
      false,
    );
    // Speed (power-up or held boost): a second step every other tick, i.e. 1.5x speed.
    if (this.tick % 2 === 0) {
      const fast = this.players.filter((p) => p.alive && (p.speedUntil > this.tick || this.canBoost(p)) && p.frozenUntil <= this.tick);
      if (fast.length) this.advance(fast, new Map(), ev, true);
    }
    if (rules.boost) this.payForBoost(ev);
    if (rules.hill) this.scoreHill();
    if (this.shockedNow.size) {
      // Reported after every move and trim, so clients can simply cut each body to this length.
      for (const slot of this.shockedNow) {
        const p = this.players[slot]!;
        if (p.alive) ev.shocked.push({ slot, length: p.body.length });
      }
      this.shockedNow.clear();
    }

    // forEach, not for-of over entries: no [cell, food] array per piece per tick (the server runs this
    // for every match). Same order, and deleting the current entry is safe either way.
    this.food.forEach((f, cell) => {
      if (f.expiresAt !== null && f.expiresAt <= this.tick) {
        this.food.delete(cell);
        ev.foodRemoved.push(cell);
      }
    });
    let normal = 0;
    let golden = false;
    let powerups = 0;
    let electric = false;
    let fire = false;
    for (const f of this.food.values()) {
      if (f.kind === FOOD_NORMAL) normal++;
      else if (f.kind === FOOD_GOLDEN) golden = true;
      else if (isPowerup(f.kind)) {
        powerups++;
        if (f.kind === FOOD_ELECTRIC) electric = true;
        else if (f.kind === FOOD_FIRE) fire = true;
      }
    }
    // Snakes have moved since food last spawned.
    this.blockedFresh = false;
    for (; normal < this.config.foodTarget; normal++) {
      const f = this.spawnFood(FOOD_NORMAL);
      if (!f) break;
      ev.foodAdded.push(f);
    }
    if (!golden && this.rng.next() < GOLDEN_SPAWN_CHANCE) {
      const f = this.spawnFood(FOOD_GOLDEN);
      if (f) ev.foodAdded.push(f);
    }
    if (rules.respawn) {
      this.respawnDue(ev);
      this.blockedFresh = false;
    }
    if (rules.ctf) this.ensureTrophy(ev);

    // Power-ups draw from the RNG only when enabled, so classic replays stay bit-identical.
    if (rules.powerups && powerups < (rules.maxPowerups ?? MAX_POWERUPS) && this.rng.next() < (rules.powerupChance ?? POWERUP_SPAWN_CHANCE)) {
      // At most one electric shock and one fire on the board at a time: they're the strongest.
      const kinds: FoodKind[] = [...POWERUP_KINDS];
      if (rules.electric && !electric) kinds.push(FOOD_ELECTRIC);
      if (rules.fire && !fire) kinds.push(FOOD_FIRE);
      const f = this.spawnFood(kinds[this.rng.int(kinds.length)]!);
      if (f) ev.foodAdded.push(f);
    }

    this.checkFinished(ev);
    return ev;
  }

  /**
   * Move `who` one cell, resolve collisions, apply deaths, then eat. The main
   * pass moves every snake; the speed-boost pass (`extra`) moves boosted ones again.
   *
   * Collisions are resolved against the board after tails that are about to move
   * have been vacated. Ghosts are intangible: they neither block others nor
   * collide with them (only with themselves). Armor turns one crash into a bounce.
   */
  private advance(who: PlayerState[], deaths: Map<number, Death>, ev: TickEvents, extra: boolean): void {
    const { width, height } = this.config;
    const rules = this.config.rules ?? {};
    const inset = zoneInset(this.config, this.tick);
    const movers: Array<{ p: PlayerState; head: number; pops: boolean; ghost: boolean; ahead?: number }> = [];
    const bounced = new Set<PlayerState>();
    const crash = (p: PlayerState, d: Death) => {
      if (p.armor) {
        p.armor = false;
        p.shieldUntil = Math.max(p.shieldUntil, this.tick + ARMOR_GRACE_TICKS);
        bounced.add(p);
      } else deaths.set(p.slot, d);
    };

    for (const p of who) {
      const was = p.dir;
      const next = p.pendingDirs.shift();
      if (next !== undefined && !isOpposite(next, p.dir)) p.dir = next;
      const head = p.body[0]!;
      // Where the snake would have gone without this turn (for close calls).
      let ahead: number | undefined;
      if (p.dir !== was) {
        let ax = (head % width) + DX[was];
        let ay = Math.floor(head / width) + DY[was];
        if (rules.wrap) {
          ax = (ax + width) % width;
          ay = (ay + height) % height;
        }
        if (ax >= 0 && ay >= 0 && ax < width && ay < height) ahead = ay * width + ax;
      }
      if (p.teleportPending) {
        p.teleportPending = false;
        const warp = this.findWarp(p);
        if (warp !== null) {
          movers.push({ p, head: warp, pops: p.grow === 0, ghost: this.isGhost(p) });
          continue;
        }
      }
      let x = (head % width) + DX[p.dir];
      let y = Math.floor(head / width) + DY[p.dir];
      if (rules.wrap) {
        x = (x + width) % width;
        y = (y + height) % height;
      } else if (x < 0 || y < 0 || x >= width || y >= height) {
        crash(p, { cause: 'wall', killedBy: null });
        continue;
      }
      if (this.obstacles.has(y * width + x)) {
        crash(p, { cause: 'wall', killedBy: null });
        continue;
      }
      if (inset && !insideZone(x, y, width, height, inset)) {
        deaths.set(p.slot, { cause: 'zone', killedBy: null });
        continue;
      }
      movers.push({ p, head: y * width + x, pops: p.grow === 0, ghost: this.isGhost(p), ahead });
    }

    // Board after tails have moved. Bodies of everyone alive at tick start still block.
    const occ = this.occupancy;
    occ.fill(-1);
    for (const p of this.players) {
      if (!p.alive || this.isGhost(p)) continue;
      const mover = movers.find((m) => m.p === p);
      const len = mover?.pops ? p.body.length - 1 : p.body.length;
      for (let i = 0; i < len; i++) occ[p.body[i]!] = p.slot;
    }

    const headCounts = new Map<number, number>();
    for (const m of movers) if (!m.ghost) headCounts.set(m.head, (headCounts.get(m.head) ?? 0) + 1);

    for (const m of movers) {
      if (m.ghost) {
        const len = m.pops ? m.p.body.length - 1 : m.p.body.length;
        const at = m.p.body.indexOf(m.head);
        if (at !== -1 && at < len) crash(m.p, { cause: 'self', killedBy: null });
        continue;
      }
      if ((headCounts.get(m.head) ?? 0) > 1) {
        // Head-on with one burning snake: it burns the others and carries on.
        const meeting = movers.filter((o) => !o.ghost && o.head === m.head);
        const burners = meeting.filter((o) => this.isBurning(o.p));
        const burner = burners.length === 1 ? burners[0]! : null;
        if (burner && meeting.every((o) => o === burner || this.canBurn(burner.p, o.p))) {
          if (burner !== m) deaths.set(m.p.slot, { cause: 'fire', killedBy: burner.p.slot });
          continue;
        }
        crash(m.p, { cause: 'head_on', killedBy: null });
        continue;
      }
      const owner = occ[m.head]!;
      if (owner === -1) continue;
      if (owner === m.p.slot) {
        crash(m.p, { cause: 'self', killedBy: null });
        continue;
      }
      const victim = this.players[owner]!;
      // A burning snake running into a rival burns it up and goes straight through.
      if (this.isBurning(m.p) && this.canBurn(m.p, victim)) deaths.set(owner, { cause: 'fire', killedBy: m.p.slot });
      else crash(m.p, { cause: this.isBurning(victim) ? 'fire' : 'snake', killedBy: owner });
    }
    for (const p of bounced) this.bounceTurn(p);

    // Close call: turned away from a rival's body that was right in front, and lived.
    if (this.phase === 'running') {
      const scale = rules.pointScale ?? 1;
      for (const m of movers) {
        if (m.ahead === undefined || m.ghost || deaths.has(m.p.slot) || bounced.has(m.p)) continue;
        const owner = occ[m.ahead]!;
        if (owner === -1 || owner === m.p.slot || this.tick - m.p.lastCloseCall < CLOSE_CALL_COOLDOWN_TICKS) continue;
        m.p.lastCloseCall = this.tick;
        m.p.score += CLOSE_CALL_POINTS * scale;
        ev.bonuses.push({ slot: m.p.slot, kind: 'close_call', points: CLOSE_CALL_POINTS * scale });
      }
    }

    for (const m of movers) {
      if (deaths.has(m.p.slot) || bounced.has(m.p)) continue;
      m.p.body.unshift(m.head);
      if (m.pops) m.p.body.pop();
      else m.p.grow--;
      (extra ? ev.extraMoves : ev.moved).push({ slot: m.p.slot, head: m.head, tailPopped: m.pops });
    }
    this.burnTouching(deaths);

    this.applyDeaths(deaths, ev);

    const moved = new Set(movers.filter((m) => m.p.alive && !bounced.has(m.p)).map((m) => m.p));
    // Food dropped this very tick (a death's remains) can't be grabbed from the side yet: the
    // wire sends removals before additions, so eating it now would leave it on clients' boards.
    const justDropped = (cell: number) => ev.foodAdded.some((f) => f.cell === cell);
    for (const p of extra ? movers.map((m) => m.p) : this.players) {
      if (!p.alive) continue;
      const head = p.body[0]!;
      if (rules.combo && p.multiplier > 1 && this.tick > p.comboUntil) p.multiplier = 1;
      this.eat(p, head, ev);
      // Wide mouth: food on either side of a head that just moved is eaten too, so grazing
      // a fruit counts (the head is drawn wider than its cell, so a graze looks like a bite).
      if (moved.has(p)) {
        const hx = head % width;
        const hy = Math.floor(head / width);
        for (const side of [-1, 1]) {
          let x = hx + DY[p.dir] * -side;
          let y = hy + DX[p.dir] * side;
          if (rules.wrap) {
            x = (x + width) % width;
            y = (y + height) % height;
          } else if (x < 0 || y < 0 || x >= width || y >= height) continue;
          const cell = y * width + x;
          const f = this.food.get(cell);
          if (!f || justDropped(cell) || this.players.some((o) => o.alive && o.body[0] === cell)) continue;
          this.eat(p, cell, ev);
        }
      }
      if (p.magnetUntil > this.tick) {
        // Magnet: also vacuum up plain food around the head (never another snake's head cell).
        const hx = head % width;
        const hy = Math.floor(head / width);
        const r = rules.magnetRadius ?? 1;
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const x = hx + dx;
            const y = hy + dy;
            if ((dx === 0 && dy === 0) || x < 0 || y < 0 || x >= width || y >= height) continue;
            const cell = y * width + x;
            const f = this.food.get(cell);
            if (!f || !isEdible(f.kind) || justDropped(cell) || this.players.some((o) => o.alive && o.body[0] === cell)) continue;
            this.eat(p, cell, ev);
          }
        }
      }
      if (rules.ctf) this.checkCapture(p);
      p.maxLength = Math.max(p.maxLength, p.body.length + p.grow);
    }
  }

  /** Boosting costs length: every BOOST_COST_TICKS a tail segment turns into food (and its points). */
  private payForBoost(ev: TickEvents): void {
    const scale = this.config.rules?.pointScale ?? 1;
    for (const p of this.players) {
      if (!this.canBoost(p) || p.frozenUntil > this.tick) continue;
      if (++p.boostTicks % BOOST_COST_TICKS !== 0) continue;
      p.score = Math.max(0, p.score - scale);
      if (p.grow > 0) {
        p.grow--;
        continue;
      }
      const tail = p.body.pop()!;
      ev.trimmed.push(p.slot);
      if (!this.food.has(tail) && !this.players.some((o) => o.alive && o.body.includes(tail))) {
        const f: Food = { cell: tail, kind: FOOD_REMAINS, expiresAt: this.tick + REMAINS_LIFETIME_TICKS };
        this.food.set(tail, f);
        ev.foodAdded.push(f);
      }
    }
  }

  /** Armor absorbed a crash: the snake stays put this tick and turns toward the more open side. */
  private bounceTurn(p: PlayerState): void {
    const { width, height } = this.config;
    const wrap = !!this.config.rules?.wrap;
    const head = p.body[0]!;
    const hx = head % width;
    const hy = Math.floor(head / width);
    let best: Dir | null = null;
    let bestRun = 0;
    for (const d of [((p.dir + 1) % 4) as Dir, ((p.dir + 3) % 4) as Dir]) {
      let run = 0;
      for (let i = 1; i <= 6; i++) {
        let x = hx + DX[d] * i;
        let y = hy + DY[d] * i;
        if (wrap) {
          x = (x + width) % width;
          y = (y + height) % height;
        } else if (x < 0 || y < 0 || x >= width || y >= height) break;
        const c = y * width + x;
        if (this.obstacles.has(c) || this.occupancy[c] !== -1) break;
        run++;
      }
      if (run > bestRun) {
        best = d;
        bestRun = run;
      }
    }
    if (best !== null) p.dir = best;
    p.pendingDirs.length = 0;
  }

  /** Teleport target: an open cell with room to keep going straight, away from other heads. */
  private findWarp(p: PlayerState): number | null {
    const { width, height } = this.config;
    const blocked = new Set<number>(this.obstacles);
    const heads: number[] = [];
    for (const q of this.players) {
      if (!q.alive) continue;
      for (const c of q.body) blocked.add(c);
      if (q !== p) heads.push(q.body[0]!);
    }
    const inset = zoneInset(this.config, this.tick + 24) + 2;
    const spanX = width - 2 * inset;
    const spanY = height - 2 * inset;
    if (spanX <= 0 || spanY <= 0) return null;
    for (let attempt = 0; attempt < 60; attempt++) {
      const x = inset + this.rng.int(spanX);
      const y = inset + this.rng.int(spanY);
      let clear = true;
      for (let i = 0; i < 6 && clear; i++) {
        const cx = x + DX[p.dir] * i;
        const cy = y + DY[p.dir] * i;
        clear = cx >= inset && cy >= inset && cx < width - inset && cy < height - inset && !blocked.has(cy * width + cx);
      }
      if (!clear || heads.some((h) => Math.abs((h % width) - x) + Math.abs(Math.floor(h / width) - y) < 6)) continue;
      return y * width + x;
    }
    return null;
  }

  /** King of the hill: a lone holder (or a single team) scores a point every HILL_POINT_EVERY ticks on the hill. */
  private scoreHill(): void {
    const hill = hillAt(this.config, this.tick);
    if (!hill) return;
    const w = this.config.width;
    const on = this.players.filter((p) => p.alive && p.frozenUntil <= this.tick && inRect(hill, p.body[0]! % w, Math.floor(p.body[0]! / w)));
    if (!on.length) return;
    const contested = this.config.mode === 'teams' ? new Set(on.map((p) => p.team)).size > 1 : on.length > 1;
    if (contested) return;
    for (const p of on) {
      p.hillTicks++;
      if (p.hillTicks % HILL_POINT_EVERY === 0) p.score += this.config.rules?.pointScale ?? 1;
    }
  }

  /** Capture the food: a carrier whose head reaches its own base scores. */
  private checkCapture(p: PlayerState): void {
    if (!p.carrying) return;
    const base = ctfBases(this.config)[p.team];
    const head = p.body[0]!;
    if (!base || !inRect(base, head % this.config.width, Math.floor(head / this.config.width))) return;
    p.carrying = false;
    p.captures++;
    p.score += CAPTURE_POINTS * (this.config.rules?.pointScale ?? 1);
  }

  /** Capture the food: put the trophy back in the middle when nobody has it and it isn't lying on the board. */
  private ensureTrophy(ev: TickEvents): void {
    if (this.players.some((p) => p.alive && p.carrying)) return;
    for (const f of this.food.values()) if (f.kind === FOOD_TROPHY) return;
    const f = this.placeTrophy(trophyHome(this.config));
    if (f) ev.foodAdded.push(f);
  }

  /** Drop the trophy on `near`, or the closest free cell to it. */
  private placeTrophy(near: number): Food | null {
    const { width, height } = this.config;
    const taken = new Set<number>();
    for (const p of this.players) if (p.alive) for (const c of p.body) taken.add(c);
    const nx = near % width;
    const ny = Math.floor(near / width);
    for (let r = 0; r < 6; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const x = nx + dx;
          const y = ny + dy;
          if (x < 1 || y < 1 || x >= width - 1 || y >= height - 1) continue;
          const cell = y * width + x;
          if (taken.has(cell) || this.food.has(cell) || this.obstacles.has(cell)) continue;
          const f: Food = { cell, kind: FOOD_TROPHY, expiresAt: null };
          this.food.set(cell, f);
          return f;
        }
      }
    }
    return null;
  }

  /**
   * Electric shock: every other snake (not teammates, not spawn-shielded ones) is cut to half
   * its length, never below the starting length. Scores are untouched and nothing is dropped.
   */
  private shockAll(p: PlayerState): void {
    const min = this.config.startLength;
    for (const q of this.players) {
      if (q === p || !q.alive || q.shieldUntil > this.tick || (this.config.mode === 'teams' && q.team === p.team)) continue;
      const len = Math.max(min, Math.ceil(q.body.length / 2));
      q.grow = Math.floor(q.grow / 2);
      if (len >= q.body.length) continue;
      q.body.length = len;
      this.shockedNow.add(q.slot);
    }
  }

  private isBurning(p: PlayerState): boolean {
    return p.alive && p.fireUntil > this.tick;
  }

  /** Fire spares teammates, ghosts, spawn-shielded snakes and other burning snakes. */
  private canBurn(burner: PlayerState, q: PlayerState): boolean {
    return q !== burner && q.alive && !this.isGhost(q) && !this.isBurning(q) && !(this.config.mode === 'teams' && q.team === burner.team);
  }

  /**
   * Fire: a rival whose head ends up touching a burning snake's head (side by side, or nose to
   * nose) burns. Running into its body already kills, like any body; this is the touch on top.
   */
  private burnTouching(deaths: Map<number, Death>): void {
    const { width } = this.config;
    for (const p of this.players) {
      if (!this.isBurning(p) || deaths.has(p.slot)) continue;
      const hx = p.body[0]! % width;
      const hy = Math.floor(p.body[0]! / width);
      for (const q of this.players) {
        if (deaths.has(q.slot) || !this.canBurn(p, q)) continue;
        const h = q.body[0]!;
        if (Math.abs((h % width) - hx) + Math.abs(Math.floor(h / width) - hy) === 1) deaths.set(q.slot, { cause: 'fire', killedBy: p.slot });
      }
    }
  }

  /** Freeze: rivals whose head is near the eater's stop moving for a moment. */
  private freezeAround(p: PlayerState): void {
    const w = this.config.width;
    const hx = p.body[0]! % w;
    const hy = Math.floor(p.body[0]! / w);
    for (const q of this.players) {
      if (q === p || !q.alive || (this.config.mode === 'teams' && q.team === p.team)) continue;
      const h = q.body[0]!;
      if (Math.abs((h % w) - hx) + Math.abs(Math.floor(h / w) - hy) > FREEZE_RADIUS) continue;
      q.frozenUntil = this.tick + FREEZE_TICKS;
      q.pendingDirs.length = 0;
    }
  }

  private respawnDue(ev: TickEvents): void {
    for (const p of this.players) {
      if (p.alive || p.respawnAt === null || p.respawnAt > this.tick) continue;
      const { body, dir } = this.findSpawn(this.spawnSide(p.team), this.lengthFor(p));
      // A handicapped bot starts each life from nothing: its score can't run away over a long arena.
      if (p.isBot && this.config.rules?.botHandicap) p.score = 0;
      p.alive = true;
      p.body = body;
      p.dir = dir;
      p.pendingDirs = [];
      p.grow = 0;
      p.multiplier = 1;
      p.comboUntil = 0;
      p.ghostUntil = 0;
      p.doubleUntil = 0;
      p.magnetUntil = 0;
      p.speedUntil = 0;
      p.frozenUntil = 0;
      p.fireUntil = 0;
      p.armor = false;
      p.teleportPending = false;
      p.carrying = false;
      p.boostTicks = 0;
      p.shieldUntil = this.config.rules?.spawnShield ? this.tick + SHIELD_TICKS : 0;
      p.respawnAt = null;
      p.deathTick = null;
      p.deathCause = null;
      p.killedBy = null;
      for (const c of body) {
        if (this.food.delete(c)) ev.foodRemoved.push(c);
      }
      ev.respawned.push({ slot: p.slot, body: [...body], dir });
    }
  }

  /** Consume the food on `cell` (if any) for player `p`. */
  private eat(p: PlayerState, cell: number, ev: TickEvents): void {
    const f = this.food.get(cell);
    if (!f) return;
    const rules = this.config.rules ?? {};
    this.food.delete(cell);
    ev.foodRemoved.push(cell);
    if (f.kind === FOOD_TROPHY) {
      // Picking up the trophy is worth nothing until it's delivered.
      p.carrying = true;
      ev.eaten.push({ slot: p.slot, cell, kind: f.kind, points: 0, multiplier: p.multiplier });
      return;
    }
    const value = FOOD_VALUE[f.kind];
    if (rules.combo) {
      if (p.foodEaten > 0 && this.tick <= p.comboUntil) p.multiplier = Math.min(MAX_MULTIPLIER, p.multiplier + 1);
      p.comboUntil = this.tick + COMBO_WINDOW_TICKS;
      p.maxMultiplier = Math.max(p.maxMultiplier, p.multiplier);
    }
    const doubled = p.doubleUntil > this.tick ? 2 : 1;
    // Bots with a handicap score less and (all but the boss) stop growing, so a new player can catch them.
    const handicap = p.isBot ? rules.botHandicap : undefined;
    const points = Math.round(value * (rules.combo ? p.multiplier : 1) * doubled * (rules.pointScale ?? 1) * (handicap?.pointShare ?? 1));
    if (!handicap || p.boss || p.body.length + p.grow < handicap.maxLength) p.grow += value;
    p.score += points;
    p.foodEaten++;
    if (f.kind === FOOD_GHOST) p.ghostUntil = this.tick + (rules.ghostTicks ?? POWERUP_DURATION_TICKS);
    else if (f.kind === FOOD_DOUBLE) p.doubleUntil = this.tick + POWERUP_DURATION_TICKS;
    else if (f.kind === FOOD_MAGNET) p.magnetUntil = this.tick + POWERUP_DURATION_TICKS;
    else if (f.kind === FOOD_SPEED) p.speedUntil = this.tick + POWERUP_DURATION_TICKS;
    else if (f.kind === FOOD_ARMOR) p.armor = true;
    else if (f.kind === FOOD_FREEZE) this.freezeAround(p);
    else if (f.kind === FOOD_TELEPORT) p.teleportPending = true;
    else if (f.kind === FOOD_ELECTRIC) this.shockAll(p);
    else if (f.kind === FOOD_FIRE) p.fireUntil = this.tick + FIRE_TICKS;
    if (isPowerup(f.kind)) p.powerupsTaken++;
    ev.eaten.push({ slot: p.slot, cell, kind: f.kind, points, multiplier: p.multiplier });
    if (rules.obstacles && p.foodEaten % OBSTACLE_EVERY === 0 && this.obstacles.size < MAX_OBSTACLES) {
      const rock = this.spawnObstacle();
      if (rock !== null) ev.obstaclesAdded.push(rock);
    }
  }

  private applyDeaths(deaths: Map<number, Death>, ev: TickEvents): void {
    if (!deaths.size) return;
    const occupied = new Set<number>();
    for (const p of this.players) {
      if (p.alive && !deaths.has(p.slot)) for (const c of p.body) occupied.add(c);
    }
    const rules = this.config.rules ?? {};
    const scale = rules.pointScale ?? 1;
    // Bounty: the leader as the tick began (a clear leader with points, free-for-all respawn arenas only).
    let leader: number | null = null;
    if (rules.respawn && this.config.mode === 'ffa') {
      let best = 0;
      for (const o of this.players) {
        if (!this.isActive(o)) continue;
        if (o.score > best) {
          best = o.score;
          leader = o.slot;
        } else if (o.score === best) leader = null;
      }
    }
    for (const [slot, d] of deaths) {
      const p = this.players[slot]!;
      p.alive = false;
      p.deathTick = this.tick;
      p.deathCause = d.cause;
      p.killedBy = d.killedBy;
      p.pendingDirs.length = 0;
      p.deaths++;
      if (p.livesLeft !== null) p.livesLeft = Math.max(0, p.livesLeft - 1);
      // Respawn arenas: come back shortly, unless the player left or has no lives left.
      const canReturn = this.config.rules?.respawn && d.cause !== 'disconnect' && this.phase === 'running' && p.livesLeft !== 0;
      p.respawnAt = canReturn ? this.tick + (p.boss ? BOSS_RESPAWN_TICKS : RESPAWN_TICKS) : null;
      ev.died.push({ slot, cause: d.cause, killedBy: d.killedBy });
      if (d.killedBy !== null && d.killedBy !== slot) {
        const killer = this.players[d.killedBy]!;
        killer.kills++;
        // Bots with a handicap get their share of the kill too: arena bots kill each other all the time.
        killer.score += Math.round(KILL_BONUS * scale * (killer.isBot ? (rules.botHandicap?.pointShare ?? 1) : 1));
        if (rules.respawn) {
          if (killer.nemesis === slot) {
            killer.nemesis = null;
            killer.score += REVENGE_BONUS * scale;
            ev.bonuses.push({ slot: killer.slot, kind: 'revenge', points: REVENGE_BONUS * scale });
          }
          if (slot === leader) {
            killer.score += BOUNTY_BONUS * scale;
            ev.bonuses.push({ slot: killer.slot, kind: 'bounty', points: BOUNTY_BONUS * scale });
          }
          if (p.boss) {
            killer.score += BOSS_BONUS * scale;
            ev.bonuses.push({ slot: killer.slot, kind: 'boss', points: BOSS_BONUS * scale });
          }
          p.nemesis = killer.slot;
        }
      }
      // A carrier drops the trophy where it fell.
      if (p.carrying) {
        p.carrying = false;
        const head = p.body[0];
        if (head !== undefined && !occupied.has(head) && !this.food.has(head)) {
          const f: Food = { cell: head, kind: FOOD_TROPHY, expiresAt: null };
          this.food.set(head, f);
          ev.foodAdded.push(f);
        }
      }
      // Dead snakes leave a trail of food behind (every second segment); big snakes leave big food.
      if (this.phase === 'running') {
        const kind = p.body.length >= BIG_REMAINS_LENGTH ? FOOD_REMAINS_BIG : FOOD_REMAINS;
        for (let i = 0; i < p.body.length; i += 2) {
          const cell = p.body[i]!;
          if (occupied.has(cell) || this.food.has(cell)) continue;
          if (!insideZone(cell % this.config.width, Math.floor(cell / this.config.width), this.config.width, this.config.height, zoneInset(this.config, this.tick))) continue;
          const f: Food = { cell, kind, expiresAt: this.tick + REMAINS_LIFETIME_TICKS };
          this.food.set(cell, f);
          ev.foodAdded.push(f);
        }
      }
    }
  }

  /** Mark the cells food may not spawn on: every body, and the cell straight ahead of every head. */
  private markBlocked(): void {
    const { width, height } = this.config;
    const at = this.blockedAt;
    if (++this.blockedStamp === 0xffffffff) {
      at.fill(0);
      this.blockedStamp = 1;
    }
    const stamp = this.blockedStamp;
    for (const p of this.players) {
      if (!p.alive) continue;
      for (const c of p.body) at[c] = stamp;
      // Keep food from spawning directly in front of a head (unfair "free" food).
      const head = p.body[0]!;
      const x = (head % width) + DX[p.dir];
      const y = Math.floor(head / width) + DY[p.dir];
      if (x >= 0 && y >= 0 && x < width && y < height) at[y * width + x] = stamp;
    }
    this.blockedFresh = true;
  }

  private spawnFood(kind: Food['kind']): Food | null {
    const { width, height } = this.config;
    if (!this.blockedFresh) this.markBlocked();
    const blocked = this.blockedAt;
    const stamp = this.blockedStamp;
    // In battle royale, food spawns inside the zone that will exist a few seconds from now.
    const inset = zoneInset(this.config, this.tick + 24);
    const spanX = width - 2 - inset * 2;
    const spanY = height - 2 - inset * 2;
    if (spanX <= 0 || spanY <= 0) return null;
    for (let attempt = 0; attempt < 64; attempt++) {
      const x = 1 + inset + this.rng.int(spanX);
      const y = 1 + inset + this.rng.int(spanY);
      const cell = y * width + x;
      if (blocked[cell] === stamp || this.food.has(cell) || this.obstacles.has(cell)) continue;
      const f: Food = {
        cell,
        kind,
        expiresAt: kind === FOOD_GOLDEN ? this.tick + GOLDEN_LIFETIME_TICKS : isPowerup(kind) ? this.tick + POWERUP_LIFETIME_TICKS : null,
      };
      this.food.set(cell, f);
      return f;
    }
    return null;
  }

  /** Place a rock away from every head (at least 4 cells) and off bodies/food. */
  private spawnObstacle(): number | null {
    const { width, height } = this.config;
    const occupied = new Set<number>();
    const heads: Array<[number, number]> = [];
    for (const p of this.players) {
      if (!p.alive) continue;
      for (const c of p.body) occupied.add(c);
      heads.push([p.body[0]! % width, Math.floor(p.body[0]! / width)]);
    }
    for (let attempt = 0; attempt < 64; attempt++) {
      const x = 1 + this.rng.int(width - 2);
      const y = 1 + this.rng.int(height - 2);
      const cell = y * width + x;
      if (occupied.has(cell) || this.food.has(cell) || this.obstacles.has(cell)) continue;
      if (heads.some(([hx, hy]) => Math.abs(hx - x) + Math.abs(hy - y) < 5)) continue;
      this.obstacles.add(cell);
      return cell;
    }
    return null;
  }

  /** Capture the food: trophies delivered per team. */
  teamCaptures(): number[] {
    const caps = [0, 0];
    for (const p of this.players) caps[p.team] = (caps[p.team] ?? 0) + p.captures;
    return caps;
  }

  private checkFinished(ev: TickEvents): void {
    // In respawn arenas a dead snake that is coming back still counts as in the game.
    const alive = this.players.filter((p) => this.isActive(p));
    let done: boolean;
    if (this.players.length === 1) {
      done = alive.length === 0;
    } else if (this.config.mode === 'teams') {
      done = new Set(alive.map((p) => p.team)).size <= 1;
    } else {
      done = alive.length <= 1;
    }
    if (this.config.rules?.ctf && this.teamCaptures().some((c) => c >= CAPTURES_TO_WIN)) done = true;
    if (this.tick >= this.config.countdownTicks + this.config.durationTicks) done = true;
    if (!done) return;
    this.phase = 'finished';
    ev.finished = true;
    this.result = this.computeResult();
  }

  private computeResult(): GameResult {
    const respawn = !!this.config.rules?.respawn;
    // Respawn arenas are a score race; elsewhere surviving longest matters most.
    const rankKey = respawn
      ? (p: PlayerState) => [p.score, p.kills, -p.deaths]
      : (p: PlayerState) => [p.alive ? 1 : 0, p.deathTick ?? this.tick, p.score, p.kills];
    const cmp = (a: number[], b: number[]) => {
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return b[i]! - a[i]!;
      return 0;
    };
    const sorted = [...this.players].sort((a, b) => cmp(rankKey(a), rankKey(b)));
    const standings: Standing[] = [];
    let place = 1;
    sorted.forEach((p, i) => {
      if (i > 0 && cmp(rankKey(sorted[i - 1]!), rankKey(p)) !== 0) place = i + 1;
      standings.push({
        slot: p.slot,
        userId: p.userId,
        name: p.name,
        team: p.team,
        place,
        score: p.score,
        kills: p.kills,
        maxLength: p.maxLength,
        foodEaten: p.foodEaten,
        survivedTicks: Math.max(0, (p.deathTick ?? this.tick) - this.config.countdownTicks),
        deathCause: p.deathCause,
        isBot: p.isBot ?? false,
        ...(this.config.rules?.ctf ? { captures: p.captures } : {}),
      });
    });

    if (this.config.mode === 'teams') {
      const teams = new Map<number, { alive: number; lastDeath: number; score: number }>();
      for (const p of this.players) {
        const t = teams.get(p.team) ?? { alive: 0, lastDeath: 0, score: 0 };
        if (p.alive) t.alive++;
        t.lastDeath = Math.max(t.lastDeath, p.deathTick ?? this.tick);
        t.score += p.score;
        teams.set(p.team, t);
      }
      // Respawn modes (king of the hill, capture the food) are decided by team score.
      const teamKey = (t: { alive: number; lastDeath: number; score: number }) => (respawn ? [t.score] : [t.alive > 0 ? 1 : 0, t.lastDeath, t.score]);
      const ranked = [...teams.entries()].sort(([, a], [, b]) => cmp(teamKey(a), teamKey(b)));
      const [first, second] = ranked;
      const tie = first && second && cmp(teamKey(first[1]), teamKey(second[1])) === 0;
      const winningTeam = tie || !first ? null : first[0];
      return {
        winnerSlots: winningTeam === null ? [] : this.players.filter((p) => p.team === winningTeam).map((p) => p.slot),
        winningTeam,
        standings,
        ticks: this.tick,
      };
    }

    const winnerSlots = this.players.length === 1 ? [] : standings.filter((s) => s.place === 1).map((s) => s.slot);
    // A shared first place is a draw: nobody is credited with a win.
    return {
      winnerSlots: winnerSlots.length === 1 ? winnerSlots : [],
      winningTeam: null,
      standings,
      ticks: this.tick,
    };
  }
}

type Death = { cause: DeathCause; killedBy: number | null };
